import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import ArchiveIcon from '@mui/icons-material/Archive'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'

import axios from '../../dataClient/dataClient'
import { theme } from '../../themes/theme'
import { handleDownloadResponse } from '../../util/downloadFile'
import { Container, Stackable, FirstItem, LargeItem, SmallItem, SmallItemRight } from '../styles/CategoryStyles'
import { Divider, FlexColumn, FlexRow } from '../styles/Containers'
import { TableCheckbox, TableCheckboxCell } from '../styles/Checkbox'
import BodyText from '../styles/Text'
import { ErrorMessage } from '../common/UserMessage'
import useInlineFilePreview from '../../hooks/useInlineFilePreview'
import useFileSelection from '../../hooks/useFileSelection'
import CategoryOriginalFile from './CategoryOriginalFile'
import { SelectionBar } from './FileBulkActionsBar'
import BulkArchiveFilesDialog from './BulkArchiveFilesDialog'
import FileDragGhost from './FileDragGhost'
import InlineFilePreview from './InlineFilePreview'

export const categoryOriginalFileTableStrings = {
  archive: 'Archive',
  moveToCategory: 'Move to category',
  selectAll: 'Select all files',
  orderSaveFailed: 'The new file order could not be saved.'
}

export const ORDER_ERROR_DISMISS_MS = 8000

const TableContainer = styled(FlexColumn)`
  min-width: 350px;
  flex-grow: 1;
`

const Empty = styled(BodyText)`
  display: flex;
  justify-content: center;
  padding-top: 15px;
`

const Headings = styled(FlexRow)`
  padding: 0 20px 10px;
  align-items: center;
`

const ButtonColumn = styled.div`
  min-width: ${props => (props.wide ? '66px' : '30px')};
`

const HandleColumn = styled.div`
  width: 22px;
  min-width: 22px;
`

const DraggableRow = styled.div`
  /* Fade the row content, not the element itself, so the drop line stays solid. */
  ${props => props.isDragging && `> *:first-of-type { opacity: 0.35; }`}
  ${props => props.isOver && `border-top: 2px solid ${theme.colors.primary};`}
  /* The teal drop line replaces the grey divider that would sit right above it. */
  ${props => props.beforeDropLine && `> *:last-of-type { visibility: hidden; }`}
`

const OrderError = styled.div`
  margin-bottom: 15px;
`

// Parked far offscreen but still rendered, so setDragImage can snapshot it.
const GhostHolder = styled.div`
  position: absolute;
  top: -1500px;
  left: -1500px;
`

// Append files missing from the persisted order.
function orderByFileOrder(fileList, order) {
  const byId = new Map(fileList.map(f => [f.id, f]))
  const ordered = order.map(id => byId.get(id)).filter(Boolean)
  const inOrder = new Set(order)
  const rest = fileList.filter(f => !inOrder.has(f.id))
  return [...ordered, ...rest]
}

function CategoryOriginalFileTable({
  files: filesProp,
  foiId,
  fileOrder = [],
  categoryId,
  preview = null,
  onPreviewConsumed,
  reorderable = false,
  renameable = false,
  selectable = false,
  disabled = false,
  handleRemoveFile = () => {},
  handleRenameFile = () => {},
  onMoveSelected
}) {
  // Keep the empty fallback stable for the sorting effect.
  const files = useMemo(() => filesProp ?? [], [filesProp])
  const hasFileOrder = Array.isArray(fileOrder) && fileOrder.length > 0
  // Compare file order by value rather than array identity.
  const fileOrderKey = Array.isArray(fileOrder) ? fileOrder.join('|') : ''

  // Prefer persisted order until the user selects a column sort.
  const [sortedFiles, setSortedFiles] = useState()

  const filePreview = useInlineFilePreview({ files: filesProp, preview, onPreviewConsumed })

  const [dragIndex, setDragIndex] = useState(null)
  const [overIndex, setOverIndex] = useState(null)
  const [hoveredFileIds, setHoveredFileIds] = useState(() => new Set())
  // Ids lifted by the current drag, for fading their origin rows.
  const [draggingIds, setDraggingIds] = useState(() => new Set())
  // Content of the offscreen drag-image card, armed on mousedown so it is
  // already rendered by the time dragstart calls setDragImage.
  const [ghost, setGhost] = useState(null)
  const ghostRef = useRef(null)
  // Read the drag source synchronously when dropping.
  const dragIndexRef = useRef(null)
  const dragBlockIdsRef = useRef(null)
  // Ignore synthetic leave events caused by moving the dragged DOM node.
  const droppedHoverRef = useRef(false)

  const [orderError, setOrderError] = useState(null)
  const orderErrorTimer = useRef(null)
  // Serialize fileOrder PATCHes: one in flight, later orders coalesce (last wins)
  // so two quick moves can never land on the server out of order.
  const orderPatchInFlight = useRef(false)
  const queuedOrderIdsRef = useRef(null)

  useEffect(() => {
    if (hasFileOrder) {
      setSortedFiles(orderByFileOrder([...files], fileOrder))
    } else {
      setSortedFiles([...files])
    }
  }, [files, fileOrderKey])

  useEffect(() => {
    return () => {
      if (orderErrorTimer.current) clearTimeout(orderErrorTimer.current)
    }
  }, [])

  function handleRowClick(downloadResult, file) {
    if (file.fileExtension === '.pdf') {
      filePreview.showFromResponse(downloadResult, file)
    } else {
      handleDownloadResponse(downloadResult, file.filename, 'download')
      filePreview.close()
    }
  }

  function clearOrderError() {
    if (orderErrorTimer.current) clearTimeout(orderErrorTimer.current)
    setOrderError(null)
  }

  function showOrderError(e) {
    console.error('Failed to persist file order', e)
    const status = e.response?.status
    // 400/409 carry an actionable reason (stale list, pending workflow) — show it.
    const serverMessage = [400, 409].includes(status) ? e.response?.data?.message : null
    if (orderErrorTimer.current) clearTimeout(orderErrorTimer.current)
    setOrderError(
      serverMessage ? `${categoryOriginalFileTableStrings.orderSaveFailed} ${serverMessage}` : categoryOriginalFileTableStrings.orderSaveFailed
    )
    orderErrorTimer.current = setTimeout(() => setOrderError(null), ORDER_ERROR_DISMISS_MS)
  }

  // The optimistic order is stale after a failure — show what the server holds
  // rather than trusting the possibly-outdated fileOrder prop.
  async function resyncOrder() {
    try {
      const { data } = await axios.get(`/category/${categoryId}`)
      setSortedFiles(orderByFileOrder([...files], data.data?.fileOrder ?? []))
    } catch (e) {
      console.error('Failed to reload the category file order', e)
      setSortedFiles(orderByFileOrder([...files], fileOrder))
    }
  }

  function persistOrder(reordered) {
    if (!categoryId) return
    queuedOrderIdsRef.current = reordered.map(f => f.id)
    flushOrderQueue()
  }

  function flushOrderQueue() {
    if (orderPatchInFlight.current || !queuedOrderIdsRef.current) return
    const ids = queuedOrderIdsRef.current
    queuedOrderIdsRef.current = null
    orderPatchInFlight.current = true
    axios
      .patch(`/category/${categoryId}/fileOrder`, { fileOrder: ids })
      .then(() => clearOrderError())
      .catch(e => {
        queuedOrderIdsRef.current = null
        showOrderError(e)
        resyncOrder()
      })
      .finally(() => {
        orderPatchInFlight.current = false
        flushOrderQueue()
      })
  }

  // Dragging or arrow-moving a selected row lifts the whole selection, in
  // current visual order; any other row moves alone.
  function getDragBlock(index) {
    const file = sortedFiles[index]
    if (canSelect && selection.isSelected(file.id)) {
      return sortedFiles.filter(f => selection.isSelected(f.id))
    }
    return [file]
  }

  function applyReorder(reordered) {
    if (reordered.every((f, i) => f === sortedFiles[i])) return
    // Manual ordering replaces the active column sort.
    setSortedFiles(reordered)
    persistOrder(reordered)
  }

  // The block becomes contiguous on its first arrow move and keeps its relative order.
  function moveRow(index, direction) {
    const block = getDragBlock(index)
    const blockIds = new Set(block.map(f => f.id))
    const firstIdx = sortedFiles.findIndex(f => blockIds.has(f.id))
    const rest = sortedFiles.filter(f => !blockIds.has(f.id))
    const target = firstIdx + direction
    if (target < 0 || target > rest.length) return

    const reordered = [...rest]
    reordered.splice(target, 0, ...block)
    applyReorder(reordered)
  }

  function moveBounds(index) {
    const block = getDragBlock(index)
    const blockIds = new Set(block.map(f => f.id))
    const firstIdx = sortedFiles.findIndex(f => blockIds.has(f.id))
    return { canMoveUp: firstIdx > 0, canMoveDown: firstIdx < sortedFiles.length - block.length }
  }

  // Armed on mousedown so it is painted before a dragstart needs it; a plain
  // click clears it on mouseup (a real drag suppresses mouseup — dragend clears).
  function armDragGhost(index) {
    const block = getDragBlock(index)
    setGhost({ filename: sortedFiles[index].filename, count: block.length })
  }

  function handleDragStart(e, index) {
    const blockIds = getDragBlock(index).map(f => f.id)
    dragIndexRef.current = index
    dragBlockIdsRef.current = blockIds
    setDragIndex(index)
    setDraggingIds(new Set(blockIds))
    // Swap the browser's row screenshot for the ghost card rendered offscreen on
    // mousedown (mousedown always precedes dragstart, so it is already painted).
    if (e.dataTransfer?.setDragImage && ghostRef.current) {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setDragImage(ghostRef.current, 20, 20)
    }
  }

  // A valid drop target cancels dragenter as well as dragover; both funnel here.
  function handleDragOver(e, index) {
    e.preventDefault()
    if (index !== overIndex) setOverIndex(index)
  }

  function handleDrop(index) {
    const from = dragIndexRef.current
    const blockIds = dragBlockIdsRef.current
    dragIndexRef.current = null
    dragBlockIdsRef.current = null
    setDragIndex(null)
    setOverIndex(null)
    setDraggingIds(new Set())
    if (from === null || from < 0 || from >= sortedFiles.length) return

    const movedFile = sortedFiles[from]
    // Browsers can leave :hover on rows that replaced the dragged DOM positions
    // until the pointer moves. Track the dropped block explicitly so every
    // lifted row's highlight follows it to its new position immediately.
    const idSet = new Set(blockIds ?? [movedFile.id])
    droppedHoverRef.current = true
    setHoveredFileIds(idSet)
    const block = sortedFiles.filter(f => idSet.has(f.id))
    const rest = sortedFiles.filter(f => !idSet.has(f.id))
    const reordered = [...rest]
    // The drop line is before the target row in the original list. Remove
    // lifted rows above that line from its index before inserting the block.
    const targetIndex = sortedFiles.slice(0, index).filter(f => !idSet.has(f.id)).length
    reordered.splice(targetIndex, 0, ...block)
    applyReorder(reordered)
  }

  function handleDragEnd() {
    dragIndexRef.current = null
    dragBlockIdsRef.current = null
    setDragIndex(null)
    setOverIndex(null)
    setDraggingIds(new Set())
    setGhost(null)
  }

  const isRemoved = false
  const canReorder = reorderable && !disabled && !!categoryId
  const canSelect = selectable && !disabled && !!categoryId

  const selection = useFileSelection({ files: filesProp, resetKey: categoryId })
  const [archiveOpen, setArchiveOpen] = useState(false)

  const shownIds = (sortedFiles ?? []).map(f => f.id)
  const selectedFiles = (sortedFiles ?? []).filter(f => selection.isSelected(f.id))
  const allShownSelected = selection.allSelected(shownIds)

  function handleArchived({ skipped }) {
    handleRemoveFile()
    if (skipped.length === 0) selection.clear()
  }

  const bulkActions = [
    { label: categoryOriginalFileTableStrings.archive, icon: <ArchiveIcon />, onClick: () => setArchiveOpen(true) },
    ...(onMoveSelected
      ? [{ label: categoryOriginalFileTableStrings.moveToCategory, icon: <DriveFileMoveIcon />, onClick: () => onMoveSelected(selectedFiles) }]
      : [])
  ]

  return (
    <FlexRow>
      <TableContainer gap="0">
        {canSelect && (
          <>
            <SelectionBar count={selection.selectedIds.size} actions={bulkActions} />
            <BulkArchiveFilesDialog
              open={archiveOpen}
              categoryId={categoryId}
              files={selectedFiles}
              onClose={() => setArchiveOpen(false)}
              onArchived={handleArchived}
            />
          </>
        )}
        {orderError && (
          <OrderError>
            <ErrorMessage>{orderError}</ErrorMessage>
          </OrderError>
        )}
        {canReorder && ghost && (
          <GhostHolder aria-hidden="true">
            <div ref={ghostRef} data-testid="drag-ghost">
              <FileDragGhost filename={ghost.filename} count={ghost.count} />
            </div>
          </GhostHolder>
        )}
        <Headings gap="30px">
          {canSelect && (
            <TableCheckboxCell>
              <TableCheckbox
                checked={allShownSelected}
                indeterminate={selection.someSelected(shownIds) && !allShownSelected}
                onChange={() => selection.toggleAll(shownIds)}
                inputProps={{ 'aria-label': categoryOriginalFileTableStrings.selectAll }}
              />
            </TableCheckboxCell>
          )}
          {canReorder && <HandleColumn />}
          <FirstItem>Original File Type</FirstItem>
          <Container>
            <Stackable width="100%">
              <LargeItem isRemoved={isRemoved}>Filename</LargeItem>
              <SmallItem isRemoved={isRemoved}>Upload Date</SmallItem>
              <SmallItemRight isRemoved={isRemoved}>Size</SmallItemRight>
            </Stackable>
          </Container>
          <ButtonColumn wide={renameable} data-testid="actions-header" />
        </Headings>
        <Divider />
        {sortedFiles && sortedFiles.length > 0 ? (
          sortedFiles.map((f, index) => {
            const bounds = canReorder ? moveBounds(index) : { canMoveUp: false, canMoveDown: false }
            return (
              <DraggableRow
                key={f.id}
                data-testid="table-row"
                draggable={canReorder}
                isDragging={canReorder && draggingIds.has(f.id)}
                isOver={canReorder && overIndex === index && dragIndex !== index}
                beforeDropLine={canReorder && overIndex === index + 1 && dragIndex !== index + 1}
                onMouseDown={canReorder ? () => armDragGhost(index) : undefined}
                onMouseUp={canReorder ? () => setGhost(null) : undefined}
                onDragStart={canReorder ? e => handleDragStart(e, index) : undefined}
                onDragEnter={canReorder ? e => handleDragOver(e, index) : undefined}
                onDragOver={canReorder ? e => handleDragOver(e, index) : undefined}
                onDrop={canReorder ? () => handleDrop(index) : undefined}
                onDragEnd={canReorder ? handleDragEnd : undefined}
                onMouseMove={
                  canReorder
                    ? () => {
                        if (dragIndexRef.current !== null) return
                        droppedHoverRef.current = false
                        setHoveredFileIds(new Set([f.id]))
                      }
                    : undefined
                }
                onMouseLeave={
                  canReorder
                    ? () => {
                        if (droppedHoverRef.current || dragIndexRef.current !== null) return
                        setHoveredFileIds(current => (current.has(f.id) ? new Set([...current].filter(id => id !== f.id)) : current))
                      }
                    : undefined
                }>
                <CategoryOriginalFile
                  file={f}
                  foiId={foiId}
                  reorderable={canReorder}
                  renameable={renameable}
                  selectable={canSelect}
                  selected={canSelect && selection.isSelected(f.id)}
                  onToggleSelect={() => selection.toggle(f.id)}
                  active={f.id === filePreview.activeFile?.id}
                  hovered={hoveredFileIds.has(f.id)}
                  suppressHover={canReorder}
                  onRowClick={handleRowClick}
                  onMove={direction => moveRow(index, direction)}
                  canMoveUp={bounds.canMoveUp}
                  canMoveDown={bounds.canMoveDown}
                  handleRemoveFile={handleRemoveFile}
                  handleRenameFile={handleRenameFile}
                  disabled={disabled}
                />
                <Divider />
              </DraggableRow>
            )
          })
        ) : (
          <Empty>No files available.</Empty>
        )}
      </TableContainer>
      <InlineFilePreview preview={filePreview} />
    </FlexRow>
  )
}

CategoryOriginalFileTable.propTypes = {
  files: PropTypes.array,
  foiId: PropTypes.string.isRequired,
  fileOrder: PropTypes.array,
  categoryId: PropTypes.string,
  preview: PropTypes.shape({
    fileId: PropTypes.string.isRequired,
    filename: PropTypes.string,
    page: PropTypes.number,
    nonce: PropTypes.number
  }),
  onPreviewConsumed: PropTypes.func,
  reorderable: PropTypes.bool,
  renameable: PropTypes.bool,
  selectable: PropTypes.bool,
  disabled: PropTypes.bool,
  handleRemoveFile: PropTypes.func,
  handleRenameFile: PropTypes.func,
  onMoveSelected: PropTypes.func
}

export default CategoryOriginalFileTable
